import { ref } from 'vue'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/services/firebase'

// 기본 설정값 (Firebase에 없을 경우 사용)
const DEFAULT_SETTINGS = {
  signupBonus: 1000000,       // 회원가입 보너스: 100만P
  referrerBonus: 100000,      // 추천한 사람 보상: 10만P
  refereeBonus: 100000,       // 추천받은 사람 보상: 10만P
}

// 전역 상태 (싱글톤)
const settings = ref({ ...DEFAULT_SETTINGS })
const isLoaded = ref(false)

export function usePointSettings() {
  const loading = ref(false)
  const error = ref(null)

  // 설정 불러오기
  const loadSettings = async () => {
    // 이미 로드됐으면 캐시 사용
    if (isLoaded.value) {
      return settings.value
    }

    loading.value = true
    error.value = null

    try {
      const settingsRef = doc(db, 'settings', 'points')
      const settingsSnap = await getDoc(settingsRef)

      if (settingsSnap.exists()) {
        const data = settingsSnap.data()
        settings.value = {
          signupBonus: data.signupBonus ?? DEFAULT_SETTINGS.signupBonus,
          referrerBonus: data.referrerBonus ?? DEFAULT_SETTINGS.referrerBonus,
          refereeBonus: data.refereeBonus ?? DEFAULT_SETTINGS.refereeBonus,
        }
      } else {
        // 설정이 없으면 기본값으로 생성
        await setDoc(settingsRef, {
          ...DEFAULT_SETTINGS,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
        settings.value = { ...DEFAULT_SETTINGS }
      }

      isLoaded.value = true
      return settings.value
    } catch (e) {
      console.error('포인트 설정 로드 실패:', e)
      error.value = '설정을 불러오는데 실패했습니다.'
      // 에러 시 기본값 사용
      settings.value = { ...DEFAULT_SETTINGS }
      return settings.value
    } finally {
      loading.value = false
    }
  }

  // 설정 업데이트 (관리자용)
  const updateSettings = async (newSettings) => {
    loading.value = true
    error.value = null

    try {
      const settingsRef = doc(db, 'settings', 'points')
      
      const updateData = {
        signupBonus: Number(newSettings.signupBonus),
        referrerBonus: Number(newSettings.referrerBonus),
        refereeBonus: Number(newSettings.refereeBonus),
        updatedAt: serverTimestamp()
      }

      await setDoc(settingsRef, updateData, { merge: true })

      // 로컬 상태 업데이트
      settings.value = {
        signupBonus: updateData.signupBonus,
        referrerBonus: updateData.referrerBonus,
        refereeBonus: updateData.refereeBonus,
      }

      return true
    } catch (e) {
      console.error('포인트 설정 업데이트 실패:', e)
      error.value = '설정 저장에 실패했습니다.'
      return false
    } finally {
      loading.value = false
    }
  }

  // 설정값 강제 새로고침
  const refreshSettings = async () => {
    isLoaded.value = false
    return await loadSettings()
  }

  // 포인트 포맷팅 (1,000,000 형식)
  const formatPoints = (points) => {
    return new Intl.NumberFormat('ko-KR').format(points)
  }

  // 포인트 포맷팅 (축약형: 100만)
  const formatPointsShort = (points) => {
    if (points >= 10000) {
      return (points / 10000) + '만'
    }
    return new Intl.NumberFormat('ko-KR').format(points)
  }

  return {
    settings,
    loading,
    error,
    isLoaded,
    loadSettings,
    updateSettings,
    refreshSettings,
    formatPoints,
    formatPointsShort,
    DEFAULT_SETTINGS
  }
}